// ---------------------------------------------------------------------------
// management-view-query
//
// Shared query-serialisation helpers for the Management View data flows.
// Extracted from the previously mirrored copies in
// `management-view-quality-control-api` / `management-view-overview-api`
// and the matching dep-key helpers in `use-quality-control-matrices` /
// `use-overview-highlights` (PRODOPSAI-620 — SC new-code duplication).
// ---------------------------------------------------------------------------

import type { CadenceOption, ScopeOption, WindowOption } from '@skystone/shared';

/** Common query shape shared by every Management View tab endpoint. */
export interface IManagementViewQuery {
  scope: ScopeOption;
  window: WindowOption;
  cadence: CadenceOption;
  /** PRODOPSAI-620: include archived teams. Omitted from the URL when false. */
  includeArchived?: boolean;
}

/** Serialise a `ScopeOption` to the URL-shape the backend parser expects. */
export function serialiseScope(scope: ScopeOption): string {
  if (scope.kind === 'product') return `product:${scope.productId}`;
  if (scope.kind === 'team') return `team:${scope.teamId}`;
  return 'all';
}

/**
 * Build the URLSearchParams for a Management View endpoint. The
 * `includeArchived` param is appended only when explicitly opted in, matching
 * the URL contract (defaults are never written).
 */
export function buildManagementViewParams(query: IManagementViewQuery): URLSearchParams {
  const params = new URLSearchParams({
    scope: serialiseScope(query.scope),
    window: query.window,
    cadence: query.cadence,
  });
  if (query.includeArchived === true) params.set('includeArchived', 'true');
  return params;
}

/**
 * Build the dependency-array key for a `ScopeOption`. The discriminated union
 * is serialised to a single string so `useApi`'s dependency array (a flat
 * `unknown[]`) can shallow-compare it across renders.
 */
export function scopeDepKey(scope: ScopeOption): string {
  return serialiseScope(scope);
}
